/**
 * scripsAdapter — maps real .NET DTOs to FHIR R4 shapes consumed by admin UI.
 *
 * Every mapped field cites its source. If a field is not in the cited source
 * file, it is not mapped. The adapter must stay truthful to the wire; the UI
 * contract (FHIR) is what normalises across backends.
 *
 * Removal plan: Sprint 3 swaps in native FHIR endpoints (Aidbox or Firely-
 * backed .NET). At that point services call FHIR directly and this file is
 * deleted. Components never change.
 */
import type {
  Organization,
  Practitioner,
  Identifier,
  Extension,
  ContactPoint,
  Address,
} from './fhir/types';
import type {
  ScripsOrganizationDto,
  ScripsOrganizationDataContract,
  ScripsOrganizationSettingsDto,
  DepartmentResponse,
  OrganizationContactDetailsDataContract,
} from './scrips-dto';

// ---------------------------------------------------------------------------
// Identifier/extension system URIs (private to Scrips namespace)
// ---------------------------------------------------------------------------

const SYS_LICENSE = 'https://scrips.com/license';
const SYS_RESOURCE_URI = 'https://scrips.com/resource-uri';
const EXT_LOGO_URL = 'https://scrips.com/fhir/ext/logoUrl';
const EXT_JURISDICTION = 'https://scrips.com/fhir/ext/jurisdiction';
const EXT_TENANT_ID = 'https://scrips.com/fhir/ext/tenantId';
const EXT_SUB_ORG_COUNT = 'https://scrips.com/fhir/ext/subOrganizationCount';
const EXT_CREATED_ON = 'https://scrips.com/fhir/ext/createdOn';

// ---------------------------------------------------------------------------
// Organization (v1)
// ---------------------------------------------------------------------------

/**
 * Map the preferred v1 ScripsOrganizationDto → FHIR Organization.
 * Source: Scrips.Organization.Core/Dtos/Organization/OrganizationDto.cs
 * Endpoint: GET /api/v1/Organization/{id}, POST /api/v1/Organization/list
 */
export function toFhirOrganization(dto: ScripsOrganizationDto): Organization {
  const identifier: Identifier[] = [];
  if (dto.LicenseNumber) {
    identifier.push({
      system: SYS_LICENSE,
      value: dto.LicenseNumber,
      ...(dto.LicenseType ? { type: { text: dto.LicenseType } } : {}),
      ...(dto.LicenseExpirationDate ? { period: { end: dto.LicenseExpirationDate } } : {}),
    });
  }
  if (dto.ResourceUri) {
    identifier.push({ system: SYS_RESOURCE_URI, value: dto.ResourceUri });
  }

  const extension: Extension[] = [];
  if (dto.LogoUrl) extension.push({ url: EXT_LOGO_URL, valueString: dto.LogoUrl });
  if (dto.Jurisdiction) extension.push({ url: EXT_JURISDICTION, valueString: dto.Jurisdiction });
  if (dto.TenantId) extension.push({ url: EXT_TENANT_ID, valueString: dto.TenantId });
  if (typeof dto.SubOrganizationCount === 'number') {
    extension.push({ url: EXT_SUB_ORG_COUNT, valueInteger: dto.SubOrganizationCount });
  }
  if (dto.CreatedOn) extension.push({ url: EXT_CREATED_ON, valueDateTime: dto.CreatedOn });

  return {
    resourceType: 'Organization',
    id: dto.Id,
    name: dto.Name,
    active: dto.IsActive,
    ...(dto.NameArabic ? { alias: [dto.NameArabic] } : {}),
    ...(identifier.length ? { identifier } : {}),
    ...(dto.ParentId ? { partOf: { reference: `Organization/${dto.ParentId}` } } : {}),
    ...(extension.length ? { extension } : {}),
  };
}

/**
 * Map the legacy OrganizationDataContract → FHIR Organization.
 * Includes nested contact details (phone/email/address) from the DTO's
 * ContactDetails field.
 *
 * Source: Scrips.Organization.Api/Models/DataContracts/OrganizationDataContract.cs
 * Endpoint: GET /api/Organization/{organizationId}
 */
export function toFhirOrganizationFromLegacy(
  dto: ScripsOrganizationDataContract
): Organization {
  const identifier: Identifier[] = [];
  if (dto.LicenseNumber) {
    identifier.push({
      system: SYS_LICENSE,
      value: dto.LicenseNumber,
      ...(dto.LicenseType ? { type: { text: dto.LicenseType } } : {}),
      ...(dto.LiceneseExpirationDate
        ? { period: { end: dto.LiceneseExpirationDate } }
        : {}),
    });
  }

  const telecom = contactDetailsToTelecom(dto.ContactDetails);
  const address = contactDetailsToAddress(dto.ContactDetails);

  return {
    resourceType: 'Organization',
    id: dto.OrganizationId,
    name: dto.Name,
    active: dto.IsActive,
    ...(identifier.length ? { identifier } : {}),
    ...(telecom.length ? { telecom } : {}),
    ...(address ? { address: [address] } : {}),
  };
}

/**
 * Enrich an already-mapped FHIR Organization with a separate
 * OrganizationSettings response. The settings endpoint holds address / phone /
 * billing data that the v1 OrganizationDto does not expose.
 *
 * Source: Scrips.Organization.Core/Dtos/OrganizationSettings/OrganizationSettingsDto.cs
 * Endpoint: GET /api/v1/OrganizationSettings/{id}
 */
export function mergeOrganizationSettings(
  org: Organization,
  settings: ScripsOrganizationSettingsDto
): Organization {
  const telecom: ContactPoint[] = org.telecom ? [...org.telecom] : [];
  if (settings.OfficePhone) {
    telecom.push({ system: 'phone', value: settings.OfficePhone, use: 'work' });
  }
  if (settings.Fax) {
    telecom.push({ system: 'fax', value: settings.Fax, use: 'work' });
  }

  const primaryAddress: Address | undefined =
    settings.Address || settings.City || settings.Country
      ? {
          use: 'work',
          ...(settings.Address ? { line: [settings.Address] } : {}),
          ...(settings.City ? { city: settings.City } : {}),
          ...(settings.Country ? { country: settings.Country } : {}),
          ...(settings.PoBox ? { line: [settings.PoBox] } : {}),
        }
      : undefined;

  const billingAddress: Address | undefined =
    settings.BillingAddress || settings.BillingCity || settings.BillingCountry
      ? {
          use: 'billing',
          ...(settings.BillingAddress ? { line: [settings.BillingAddress] } : {}),
          ...(settings.BillingCity ? { city: settings.BillingCity } : {}),
          ...(settings.BillingCountry ? { country: settings.BillingCountry } : {}),
        }
      : undefined;

  const address: Address[] = [];
  if (primaryAddress) address.push(primaryAddress);
  if (billingAddress) address.push(billingAddress);

  return {
    ...org,
    ...(telecom.length ? { telecom } : {}),
    ...(address.length ? { address } : {}),
  };
}

function contactDetailsToTelecom(
  c: OrganizationContactDetailsDataContract | null | undefined
): ContactPoint[] {
  if (!c) return [];
  const telecom: ContactPoint[] = [];
  if (c.Phone) telecom.push({ system: 'phone', value: c.Phone, use: 'work' });
  if (c.Email) telecom.push({ system: 'email', value: c.Email, use: 'work' });
  return telecom;
}

function contactDetailsToAddress(
  c: OrganizationContactDetailsDataContract | null | undefined
): Address | undefined {
  if (!c) return undefined;
  if (!c.AddressLine && !c.City && !c.Country) return undefined;
  return {
    use: 'work',
    ...(c.AddressLine ? { line: [c.AddressLine] } : {}),
    ...(c.City ? { city: c.City } : {}),
    ...(c.State ? { state: c.State } : {}),
    ...(c.PostalCode ? { postalCode: c.PostalCode } : {}),
    ...(c.Country ? { country: c.Country } : {}),
  };
}

// ---------------------------------------------------------------------------
// Department → FHIR Organization (type=dept)
// ---------------------------------------------------------------------------

/**
 * Map a DepartmentResponse → FHIR Organization with type=dept.
 *
 * A department is an Organization linked to its parent by `partOf` — NOT a
 * Location. Per IHE mCSD, HL7 FHIR R4 core, and Medplum's production model,
 * "departments should be Organizations (with partOf), not standalone
 * Locations" (Locations model the physical rooms layer instead). A top-level
 * department is `partOf` its practice/org node (OrganizationId); a
 * sub-department is `partOf` its parent department (ParentId).
 *
 * Note: a department carries no specialty of its own — specialty is a
 * per-person fact (DoctorSpeciality/StaffSpecialities); a department's
 * coverage is rolled up from its members. So no specialty is mapped here.
 *
 * Source: Scrips.Organization.Domain.Models/Dtos/Department/DepartmentResponse.cs
 * Endpoint: POST /api/v1/Department/List, GET /api/v1/Department/{id}
 */
export function toFhirOrganizationFromDepartment(dto: DepartmentResponse): Organization {
  const identifier: Identifier[] = [];
  if (dto.Code) identifier.push({ use: 'official', value: dto.Code });

  const telecom: ContactPoint[] = [];
  if (dto.OfficePhone) telecom.push({ system: 'phone', value: dto.OfficePhone, use: 'work' });
  if (dto.Fax) telecom.push({ system: 'fax', value: dto.Fax, use: 'work' });

  const extension: Extension[] = [];
  if (dto.LogoUrl) extension.push({ url: EXT_LOGO_URL, valueString: dto.LogoUrl });

  // Sub-department → partOf the parent department; top-level → partOf the org/practice node.
  const partOfId = dto.ParentId ?? dto.OrganizationId;

  return {
    resourceType: 'Organization',
    id: dto.Id,
    name: dto.Name,
    active: dto.IsActive,
    type: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/organization-type',
            code: 'dept',
            display: 'Hospital Department',
          },
        ],
      },
    ],
    ...(dto.NameArabic ? { alias: [dto.NameArabic] } : {}),
    ...(identifier.length ? { identifier } : {}),
    ...(telecom.length ? { telecom } : {}),
    ...(partOfId ? { partOf: { reference: `Organization/${partOfId}` } } : {}),
    ...(extension.length ? { extension } : {}),
  };
}

// ---------------------------------------------------------------------------
// Practitioner — v1 endpoint under /api/v1/Practice is not in Sprint 1 scope;
// keeping a placeholder minimal mapper so downstream sprints can fill in the
// real DTO once we clone Scrips.Practice. Do NOT invent fields here.
// ---------------------------------------------------------------------------

export interface ScripsPractitionerMinimalDto {
  Id: string;
  Name?: string;
  Email?: string;
  IsActive?: boolean;
}

export function toFhirPractitionerStub(
  dto: ScripsPractitionerMinimalDto
): Practitioner {
  const nameParts = (dto.Name ?? '').trim().split(/\s+/).filter(Boolean);
  const family = nameParts.length > 1 ? nameParts.pop()! : nameParts[0] ?? '';
  const given = nameParts.length ? nameParts : [];

  const telecom: ContactPoint[] = [];
  if (dto.Email) telecom.push({ system: 'email', value: dto.Email, use: 'work' });

  return {
    resourceType: 'Practitioner',
    id: dto.Id,
    active: dto.IsActive ?? true,
    ...(family || given.length ? { name: [{ family, given }] } : {}),
    ...(telecom.length ? { telecom } : {}),
  };
}
